<script setup lang="ts">
import { DatepickerComponent, NoteComponent, SwitchComponent } from '@metapic-apps/shared'
import { ref, watch } from 'vue'

import SharedBudgetSelector from '@/components/CampaignForm/SharedBudgetSelector.vue'
import { formatMoneyForAdmin } from '@/lib/formatters/money.formatter'
import type { AdvertiserListItemType } from '@/lib/types/advertiser.types'
import type { CampaignFormType } from '@/lib/types/campaign.types'
import {
  BoxComponent,
  FlexComponent,
  InputComponent,
  RadioButton,
  RadioButtonGroupComponent,
  TitleComponent,
  TypographyComponent,
} from '@/ui-library'

const props = defineProps<{
  currency?: string
  advertiserList: AdvertiserListItemType[]
  hasHashtags?: boolean
}>()

const hasStartNow = defineModel<CampaignFormType['hasStartNow']>('hasStartNow')
const hasOpenEnd = defineModel<CampaignFormType['hasOpenEnd']>('hasOpenEnd')
const validFrom = defineModel<CampaignFormType['validFrom']>('validFrom')
const validUntil = defineModel<CampaignFormType['validUntil']>('validUntil')
const perUserLimit = defineModel<CampaignFormType['perUserLimit']>('perUserLimit')
const maxClicks = defineModel<CampaignFormType['maxClicks']>('maxClicks')
const budgetLimit = defineModel<CampaignFormType['budgetLimit']>('budgetLimit')
const hasBudget = defineModel<CampaignFormType['hasBudget']>('hasBudget')
const sharedBudget = defineModel<CampaignFormType['sharedBudget']>('sharedBudget', { default: {} })

const perUserLimitInitial = ref(perUserLimit.value)

const DEFAULT_END_TIME = new Date(0, 0, 0, 23, 59, 0)

const disableStartDate = (date: Date) => {
  return date.getTime() < Date.now() - 8.64e7 // number of milliseconds of a day
}
const disableEndDate = (date: Date) => {
  if (validFrom.value) {
    return date.getTime() < new Date(validFrom.value).getTime()
  }
  return disableStartDate(date)
}

const removeCurrentBudget = () => {
  sharedBudget.value = undefined
}

watch(
  () => props.advertiserList?.length,
  (advertisersAmount) => {
    if (advertisersAmount !== 1) {
      removeCurrentBudget()
    }
  },
)

watch(
  () => props.hasHashtags,
  (hasHashtags) => {
    if (hasHashtags) {
      hasOpenEnd.value = false
    }
  },
  { immediate: true },
)
</script>
<template>
  <BoxComponent>
    <BoxComponent :mb="32">
      <FlexComponent :gap="24" direction="column">
        <TitleComponent title="Start" />
        <RadioButtonGroupComponent v-model="hasStartNow" children-width="0%" name="hasStartNow">
          <RadioButton label="Start now" :value="true" />
          <RadioButton label="Start on date" :value="false" />
        </RadioButtonGroupComponent>
        <el-form-item v-if="!hasStartNow" prop="validFrom">
          <DatepickerComponent
            v-model="validFrom"
            placeholder="Select start date"
            input-note="Based on UTC +01:00"
            name="validFrom"
            :disabled-date="disableStartDate"
          />
        </el-form-item>
      </FlexComponent>
    </BoxComponent>
    <BoxComponent>
      <FlexComponent :gap="24" direction="column">
        <TitleComponent title="End" />
        <NoteComponent v-if="hasHashtags"> Hashtag content fetching requires the campaign to end on an event. </NoteComponent>
        <RadioButtonGroupComponent v-model="hasOpenEnd" children-width="0%" name="hasOpenEnd">
          <RadioButton v-if="!hasHashtags" label="Open end" :value="true" />
          <RadioButton label="End on event" :value="false" />
        </RadioButtonGroupComponent>
        <template v-if="!hasOpenEnd">
          <el-form-item prop="validUntil">
            <DatepickerComponent
              v-model="validUntil"
              title="End on date"
              placeholder="Select end date"
              name="validUntil"
              input-note="Based on UTC +01:00"
              :disabled-date="disableEndDate"
              :default-time="DEFAULT_END_TIME"
            />
          </el-form-item>
          <el-form-item prop="maxClicks">
            <InputComponent v-model.integer="maxClicks" name="maxClicks">
              <template #header>
                <TypographyComponent variant="title-large">or click limit</TypographyComponent>
              </template>
              <template #suffix>
                <TypographyComponent variant="title-large">
                  {{ perUserLimit ? 'Clicks / creator' : 'Clicks / total creators' }}
                </TypographyComponent>
              </template>
            </InputComponent>
          </el-form-item>
          <SwitchComponent v-model="perUserLimit" title="Set limit for each creator" name="perUserLimit">
            <template #tooltip>
              <BoxComponent :w="280">
                <BoxComponent :mb="4">
                  <TypographyComponent variant="title-medium"> Set limit for each creator </TypographyComponent>
                </BoxComponent>
                <TypographyComponent variant="body-medium">
                  By default, the click limit applies to all creators and shared links. Choose here if you want the limit to apply
                  individually to each creator.
                </TypographyComponent>
              </BoxComponent>
            </template>
          </SwitchComponent>
          <NoteComponent v-if="perUserLimitInitial && !perUserLimit">
            Changing the limit type may affect the status of creators.
          </NoteComponent>
          <template v-if="advertiserList.length === 1">
            <el-form-item prop="hasBudget">
              <SwitchComponent v-model="hasBudget" title="Budget" name="hasBudget" />
            </el-form-item>
            <template v-if="hasBudget">
              <el-form-item prop="sharedBudget">
                <SharedBudgetSelector
                  v-model="sharedBudget"
                  :currency="currency"
                  :advertiser-id="advertiserList[0]!.id"
                  @remove-current-budget="removeCurrentBudget"
                />
              </el-form-item>
              <el-form-item prop="budgetLimit">
                <InputComponent v-model.number="budgetLimit" name="budgetLimit">
                  <template #header>
                    <TitleComponent title="Campaign limit">
                      <template #tooltip>
                        <BoxComponent :w="280">
                          <BoxComponent :mb="4">
                            <TypographyComponent variant="title-medium">Campaign limit</TypographyComponent>
                          </BoxComponent>
                          <TypographyComponent variant="body-medium">
                            You can set the maximum amount the campaign can use from the shared budget or, if no shared budget is set,
                            assign an individual budget for this campaign.
                          </TypographyComponent>
                        </BoxComponent>
                      </template>
                    </TitleComponent>
                  </template>
                  <template #suffix>
                    <TypographyComponent variant="title-large">
                      /
                      {{
                        sharedBudget?.amount
                          ? formatMoneyForAdmin(
                              { amount: sharedBudget?.amount.amount, currency: currency },
                              { allowZero: true, notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 2 },
                            )
                          : currency
                      }}
                    </TypographyComponent>
                  </template>
                </InputComponent>
              </el-form-item>
            </template>
          </template>
        </template>
      </FlexComponent>
    </BoxComponent>
  </BoxComponent>
</template>
<style lang="scss" scoped></style>
